import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import minhasInscricoesService from "../../../services/minhasInscricoesService";
import type { InscricaoDetalhada } from "../../../services/minhasInscricoesService";
import PlanilhaOrcamentaria from "../../Shared/PlanilhaOrcamentaria";
import type { DadosOrcamento } from "../../../types/orcamento";

const STATUS_COLORS = {
  pendente: "bg-yellow-100 text-yellow-800 border-yellow-300",
  em_analise: "bg-blue-100 text-blue-800 border-blue-300",
  aprovado: "bg-green-100 text-green-800 border-green-300",
  reprovado: "bg-red-100 text-red-800 border-red-300",
};

const STATUS_LABELS = {
  pendente: "Pendente",
  em_analise: "Em Análise",
  aprovado: "Aprovado",
  reprovado: "Reprovado",
};

export default function DetalhesInscricao() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [inscricao, setInscricao] = useState<InscricaoDetalhada | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const carregarInscricao = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await minhasInscricoesService.buscar(Number(id));
        setInscricao(response.inscricao);
      } catch (err) {
        console.error(err);
        setError("Erro ao carregar detalhes da inscrição");
      } finally {
        setLoading(false);
      }
    };

    void carregarInscricao();
  }, [id]);

  const renderCampoValor = (key: string, campo: any) => {
    const valor = inscricao?.dados_formulario[key];

    if (!valor && valor !== 0) {
      return <span className="text-gray-400 italic">Não informado</span>;
    }

    // Se for campo de orçamento
    if (campo.type === "orcamento") {
      const orcamentoData = valor as DadosOrcamento;
      return (
        <div className="mt-4">
          <PlanilhaOrcamentaria
            value={orcamentoData}
            onChange={() => {}} // readonly
            config={campo.config}
            readonly={true}
          />
        </div>
      );
    }

    // Se for arquivo
    if (campo.type === "file") {
      const fileData = valor as { filename: string; url: string };
      return (
        <a
          href={fileData.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          📎 {fileData.filename}
        </a>
      );
    }

    // Se for array (select múltiplo)
    if (Array.isArray(valor)) {
      return <span className="text-gray-900">{valor.join(", ")}</span>;
    }

    // Texto simples
    return <span className="text-gray-900 whitespace-pre-wrap">{String(valor)}</span>;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando detalhes...</p>
        </div>
      </div>
    );
  }

  if (error || !inscricao) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || "Inscrição não encontrada"}</p>
        </div>
        <button
          onClick={() => navigate("/inscricoes")}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          ← Voltar para Minhas Inscrições
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/inscricoes"
            className="text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center gap-2"
          >
            ← Voltar para Minhas Inscrições
          </Link>
          <div className="flex items-start justify-between mt-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Detalhes da Inscrição
              </h1>
              <p className="mt-2 text-gray-600">ID: #{inscricao.id}</p>
            </div>
            <span
              className={`px-4 py-2 inline-flex text-sm leading-5 font-semibold rounded-lg border ${
                STATUS_COLORS[inscricao.status]
              }`}
            >
              {STATUS_LABELS[inscricao.status]}
            </span>
          </div>
        </div>

        {/* Informações do Edital */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            📋 Edital
          </h2>
          <div className="space-y-3">
            <div>
              <span className="text-sm text-gray-500">Nome:</span>
              <p className="font-medium text-gray-900">{inscricao.edital.nome}</p>
            </div>
            {inscricao.edital_detalhes && (
              <>
                <div>
                  <span className="text-sm text-gray-500">Descrição:</span>
                  <p className="text-gray-900">{inscricao.edital_detalhes.descricao}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Data de Início:</span>
                    <p className="font-medium text-gray-900">
                      {new Date(inscricao.edital_detalhes.data_inicio).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Data de Término:</span>
                    <p className="font-medium text-gray-900">
                      {new Date(inscricao.edital_detalhes.data_fim).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Dados do Formulário */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            📝 Dados da Inscrição
          </h2>
          <div className="space-y-6">
            {inscricao.edital_detalhes?.schema_campos &&
              Object.entries(inscricao.edital_detalhes.schema_campos).map(([key, campo]: [string, any]) => (
                <div key={key} className="border-b border-gray-200 pb-4 last:border-b-0">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {campo.label}
                    {campo.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderCampoValor(key, campo)}
                </div>
              ))}
          </div>
        </div>

        {/* Informações de Análise */}
        {(inscricao.observacoes || inscricao.analisado_em) && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              🔍 Análise
            </h2>
            <div className="space-y-3">
              {inscricao.analisado_em && (
                <div>
                  <span className="text-sm text-gray-500">Analisado em:</span>
                  <p className="font-medium text-gray-900">
                    {new Date(inscricao.analisado_em).toLocaleDateString("pt-BR")} às{" "}
                    {new Date(inscricao.analisado_em).toLocaleTimeString("pt-BR")}
                  </p>
                </div>
              )}
              {inscricao.analisado_por && (
                <div>
                  <span className="text-sm text-gray-500">Analisado por:</span>
                  <p className="font-medium text-gray-900">
                    {inscricao.analisado_por.nome}
                  </p>
                </div>
              )}
              {inscricao.observacoes && (
                <div>
                  <span className="text-sm text-gray-500">Observações:</span>
                  <p className="text-gray-900 whitespace-pre-wrap">
                    {inscricao.observacoes}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Informações de Criação */}
        <div className="mt-6 text-sm text-gray-500 text-center">
          Inscrição criada em{" "}
          {new Date(inscricao.created_at).toLocaleDateString("pt-BR")} às{" "}
          {new Date(inscricao.created_at).toLocaleTimeString("pt-BR")}
        </div>
      </div>
    </div>
  );
}
